import { v4 as uuidv4 } from 'uuid';
import { 
  CertificateTemplate, 
  CertificateTemplateType,
  CertificateTemplatePreview,
  CertificateTemplateElement
} from '@/types/certificateTemplate';
import { Student, Program } from '@/services/db';
import { formatDistance } from 'date-fns';
import { cn } from '@/lib/utils';
import { safeFormatDate } from './utils';
import { certificateTemplateSupabaseService } from './supabaseTemplateService';

/**
 * In-memory storage for certificate templates
 * In a real app, this would be stored in a database
 */
const certificateTemplatesStore: Record<string, CertificateTemplate> = {};

/**
 * Initialize with default certificate templates
 */
export const initializeCertificateTemplates = () => {
  // Default course completion certificate template
  const courseCompletionTemplate: CertificateTemplate = {
    id: uuidv4(),
    name: 'Standard Course Completion',
    type: 'course-completion',
    description: 'Default template for course completion certificates',
    backgroundColor: '#ffffff',
    backgroundImage: 'certificate-bg-1',
    size: { width: 800, height: 600 },
    elements: [
      {
        id: uuidv4(),
        type: 'text',
        content: 'COURSE COMPLETION CERTIFICATE',
        position: { x: 400, y: 80 },
        style: {
          fontSize: '24px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'This is to certify that',
        position: { x: 400, y: 140 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'studentName',
        position: { x: 400, y: 180 },
        style: {
          fontSize: '28px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'has successfully completed the course',
        position: { x: 400, y: 220 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'courseName',
        position: { x: 400, y: 260 },
        style: {
          fontSize: '22px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'completionDate',
        position: { x: 400, y: 320 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 250, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Instructor Signature',
        position: { x: 250, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 550, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Institution Seal',
        position: { x: 550, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'qrcode',
        position: { x: 700, y: 520 },
        style: {
          width: '80px',
          height: '80px',
        },
        dynamic: true,
        dataField: 'verificationCode'
      },
    ],
    createdAt: safeFormatDate(new Date()),
    updatedAt: safeFormatDate(new Date()),
    createdBy: 'System',
    isDefault: true
  };

  // Default enrollment certificate template
  const enrollmentTemplate: CertificateTemplate = {
    id: uuidv4(),
    name: 'Standard Enrollment Certificate',
    type: 'enrollment',
    description: 'Default template for enrollment certificates',
    backgroundColor: '#f8f8f8',
    backgroundImage: 'certificate-bg-2',
    size: { width: 800, height: 600 },
    elements: [
      {
        id: uuidv4(),
        type: 'text',
        content: 'CERTIFICATE OF ENROLLMENT',
        position: { x: 400, y: 80 },
        style: {
          fontSize: '24px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'This is to certify that',
        position: { x: 400, y: 140 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'studentName',
        position: { x: 400, y: 180 },
        style: {
          fontSize: '28px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'is currently enrolled in',
        position: { x: 400, y: 220 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'programTitle',
        position: { x: 400, y: 260 },
        style: {
          fontSize: '22px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'enrollmentDate',
        position: { x: 400, y: 320 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 250, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Program Director',
        position: { x: 250, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 550, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Institution Seal',
        position: { x: 550, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'qrcode',
        position: { x: 700, y: 520 },
        style: {
          width: '80px',
          height: '80px',
        },
        dynamic: true,
        dataField: 'verificationCode'
      },
    ],
    createdAt: safeFormatDate(new Date()),
    updatedAt: safeFormatDate(new Date()),
    createdBy: 'System',
    isDefault: true
  };

  // Default professional development certificate template
  const pdTemplate: CertificateTemplate = {
    id: uuidv4(),
    name: 'Professional Development Certificate',
    type: 'professional-development',
    description: 'Default template for professional development certificates',
    backgroundColor: '#f5f5f5',
    backgroundImage: 'certificate-bg-3',
    size: { width: 800, height: 600 },
    elements: [
      {
        id: uuidv4(),
        type: 'text',
        content: 'PROFESSIONAL DEVELOPMENT CERTIFICATE',
        position: { x: 400, y: 80 },
        style: {
          fontSize: '24px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'This is to certify that',
        position: { x: 400, y: 140 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'teacherName',
        position: { x: 400, y: 180 },
        style: {
          fontSize: '28px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'has successfully completed',
        position: { x: 400, y: 220 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'trainingTitle',
        position: { x: 400, y: 260 },
        style: {
          fontSize: '22px',
          fontFamily: 'serif',
          color: '#333333',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        dynamic: true,
        dataField: 'completionDate',
        position: { x: 400, y: 320 },
        style: {
          fontSize: '16px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 250, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Organizer Signature',
        position: { x: 250, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'signature',
        position: { x: 550, y: 420 },
        style: {
          width: '150px',
          height: '60px',
        }
      },
      {
        id: uuidv4(),
        type: 'text',
        content: 'Official Seal',
        position: { x: 550, y: 470 },
        style: {
          fontSize: '14px',
          fontFamily: 'serif',
          color: '#555555',
        }
      },
      {
        id: uuidv4(),
        type: 'qrcode',
        position: { x: 700, y: 520 },
        style: {
          width: '80px',
          height: '80px',
        },
        dynamic: true,
        dataField: 'verificationCode'
      },
    ],
    createdAt: safeFormatDate(new Date()),
    updatedAt: safeFormatDate(new Date()),
    createdBy: 'System',
    isDefault: true
  };

  // Store the default templates
  certificateTemplatesStore[courseCompletionTemplate.id] = courseCompletionTemplate;
  certificateTemplatesStore[enrollmentTemplate.id] = enrollmentTemplate;
  certificateTemplatesStore[pdTemplate.id] = pdTemplate;

  console.log('Initialized default certificate templates');
  return true;
};

/**
 * Get all certificate templates with background sync
 */
export const getAllCertificateTemplates = async (): Promise<CertificateTemplate[]> => {
  try {
    // Get from localStorage immediately for instant response
    const localTemplates = Object.values(certificateTemplatesStore);
    
    // Background sync from Supabase without blocking UI
    certificateTemplateSupabaseService.syncAllTemplates().then(syncedTemplates => {
      if (syncedTemplates.length > 0) {
        // Update localStorage with synced data
        syncedTemplates.forEach(template => {
          certificateTemplatesStore[template.id] = template;
        });
        // Dispatch event to notify components of data update
        window.dispatchEvent(new CustomEvent('certificateTemplatesChanged'));
      }
    }).catch(error => {
      console.log('Background certificate templates sync failed, using local data:', error);
    });
    
    return localTemplates;
  } catch (error) {
    console.error('Error getting certificate templates:', error);
    return Object.values(certificateTemplatesStore);
  }
};

/**
 * Get certificate templates by type with background sync
 */
export const getCertificateTemplatesByType = async (type: CertificateTemplateType): Promise<CertificateTemplate[]> => {
  const allTemplates = await getAllCertificateTemplates();
  return allTemplates.filter(template => template.type === type);
};

/**
 * Get a certificate template by ID
 */
export const getCertificateTemplateById = (id: string): CertificateTemplate | null => {
  return certificateTemplatesStore[id] || null;
};

/**
 * Get the default template for a specific type
 */
export const getDefaultCertificateTemplate = async (type: CertificateTemplateType): Promise<CertificateTemplate | null> => {
  const templates = await getCertificateTemplatesByType(type);
  return templates.find(template => template.isDefault) || templates[0] || null;
};

/**
 * Create a new certificate template with Supabase sync
 */
export const createCertificateTemplate = async (template: Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<CertificateTemplate> => {
  const id = uuidv4();
  const now = safeFormatDate(new Date());
  
  const newTemplate: CertificateTemplate = {
    ...template,
    id,
    createdAt: now,
    updatedAt: now
  };
  
  // Save to localStorage immediately
  certificateTemplatesStore[id] = newTemplate;
  
  // Background sync to Supabase
  try {
    await certificateTemplateSupabaseService.saveCertificateTemplate(newTemplate);
  } catch (error) {
    console.log('Background sync failed for new certificate template:', error);
  }
  
  return newTemplate;
};

/**
 * Update a certificate template with Supabase sync
 */
export const updateCertificateTemplate = async (id: string, updates: Partial<Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'>>): Promise<CertificateTemplate | null> => {
  const template = certificateTemplatesStore[id];
  if (!template) return null;
  
  const updatedTemplate: CertificateTemplate = {
    ...template,
    ...updates,
    updatedAt: safeFormatDate(new Date())
  };
  
  // Update localStorage immediately
  certificateTemplatesStore[id] = updatedTemplate;
  
  // Background sync to Supabase
  try {
    await certificateTemplateSupabaseService.saveCertificateTemplate(updatedTemplate);
  } catch (error) {
    console.log('Background sync failed for updated certificate template:', error);
  }
  
  return updatedTemplate;
};

/**
 * Delete a certificate template with Supabase sync
 */
export const deleteCertificateTemplate = async (id: string): Promise<boolean> => {
  if (!certificateTemplatesStore[id]) return false;
  
  // Don't allow deletion of the last template of a type
  const template = certificateTemplatesStore[id];
  const templatesOfSameType = Object.values(certificateTemplatesStore).filter(t => t.type === template.type);
  
  if (templatesOfSameType.length <= 1) {
    return false;
  }
  
  // Don't allow deletion of default templates
  if (template.isDefault) {
    return false;
  }
  
  // Delete from localStorage immediately
  delete certificateTemplatesStore[id];
  
  // Background delete from Supabase
  try {
    await certificateTemplateSupabaseService.deleteCertificateTemplate(id);
  } catch (error) {
    console.log('Background delete failed for certificate template:', error);
  }
  
  return true;
};

/**
 * Set a template as the default for its type with Supabase sync
 */
export const setDefaultCertificateTemplate = async (id: string): Promise<boolean> => {
  const template = certificateTemplatesStore[id];
  if (!template) return false;
  
  // Remove default flag from other templates of the same type
  const templatesOfSameType = Object.values(certificateTemplatesStore).filter(t => t.type === template.type);
  for (const t of templatesOfSameType) {
    if (t.id !== id && t.isDefault) {
      const updatedTemplate = {
        ...t,
        isDefault: false,
        updatedAt: safeFormatDate(new Date())
      };
      certificateTemplatesStore[t.id] = updatedTemplate;
      
      // Background sync
      try {
        await certificateTemplateSupabaseService.saveCertificateTemplate(updatedTemplate);
      } catch (error) {
        console.log('Background sync failed for template default update:', error);
      }
    }
  }
  
  // Set this template as default
  const updatedTemplate = {
    ...template,
    isDefault: true,
    updatedAt: safeFormatDate(new Date())
  };
  certificateTemplatesStore[id] = updatedTemplate;
  
  // Background sync
  try {
    await certificateTemplateSupabaseService.saveCertificateTemplate(updatedTemplate);
  } catch (error) {
    console.log('Background sync failed for template default update:', error);
  }
  
  return true;
};

/**
 * Duplicate a certificate template
 */
export const duplicateCertificateTemplate = async (id: string): Promise<CertificateTemplate | null> => {
  const template = certificateTemplatesStore[id];
  if (!template) return null;
  
  const copy: Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
    ...template,
    name: `${template.name} (Copy)`,
    isDefault: false
  };
  
  return await createCertificateTemplate(copy);
};

/**
 * Get a preview list of all templates
 */
export const getCertificateTemplatePreviews = (): CertificateTemplatePreview[] => {
  return Object.values(certificateTemplatesStore).map(template => ({
    id: template.id,
    name: template.name,
    type: template.type,
    thumbnail: template.thumbnail,
    description: template.description,
    isDefault: template.isDefault
  }));
};

/**
 * Generate certificate from PDF template
 * In a real application, this would use a PDF library to fill in the template
 */
export const generateCertificateFromPDFTemplate = async (
  templateId: string,
  data: Record<string, string>
): Promise<string | null> => {
  try {
    const template = getCertificateTemplateById(templateId);
    if (!template || !template.metadata?.isPdf || !template.metadata?.pdfUrl) {
      return null;
    }
    
    // In a real application, this would use a PDF library like PDF-Lib
    // to overlay the data onto the template PDF
    // For demo purposes, we're just returning the PDF URL
    console.log('Generating certificate from PDF template with data:', data);
    
    // Return the original PDF URL (in a real app, this would be the URL to the generated PDF)
    return template.metadata.pdfUrl;
  } catch (error) {
    console.error('Error generating certificate from PDF template:', error);
    return null;
  }
};

// Export public module interfaces
export const certificateTemplateService = {
  initializeCertificateTemplates,
  getAllCertificateTemplates,
  getCertificateTemplatesByType,
  getCertificateTemplateById,
  getDefaultCertificateTemplate,
  createCertificateTemplate,
  updateCertificateTemplate,
  deleteCertificateTemplate,
  setDefaultCertificateTemplate,
  duplicateCertificateTemplate,
  getCertificateTemplatePreviews,
  generateCertificateFromPDFTemplate
};

export default certificateTemplateService;
